import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * PlayArrow component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPlayArrow extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.95074 1.46304C4.34954 1.2414 4.79835 1.125 5.25481 1.125C5.71757 1.125 6.17247 1.24463 6.57536 1.47228C6.58325 1.47673 6.59106 1.48133 6.59878 1.48607L20.0631 9.75147C20.4676 9.95474 20.809 10.2649 21.0501 10.6487C21.2986 11.0442 21.4304 11.5017 21.4304 11.9688C21.4304 12.4359 21.2986 12.8934 21.0501 13.2889C20.8093 13.6723 20.4683 13.9822 20.0644 14.1855L6.60092 22.5126C6.5925 22.5178 6.58398 22.5229 6.57536 22.5277C6.17247 22.7554 5.71757 22.875 5.25481 22.875C4.79835 22.875 4.34952 22.7586 3.95072 22.5369C3.54483 22.3301 3.20224 22.0175 2.9593 21.6318C2.71296 21.2408 2.5785 20.7898 2.57054 20.3277L2.57031 20.3148L2.57042 3.67232C2.57839 3.21024 2.71296 2.75918 2.9593 2.36816C3.20225 1.98253 3.54485 1.66984 3.95074 1.46304ZM5.25481 2.625C5.05064 2.625 4.84993 2.67778 4.67217 2.77822C4.66114 2.78445 4.64996 2.7904 4.63863 2.79607C4.47051 2.88013 4.32863 3.00867 4.22844 3.16771C4.12923 3.32518 4.07464 3.50659 4.07042 3.69258V20.3074C4.07464 20.4934 4.12923 20.6748 4.22844 20.8323C4.32863 20.9913 4.47051 21.1199 4.63863 21.2039C4.64996 21.2096 4.66114 21.2155 4.67217 21.2218C4.84993 21.3222 5.05064 21.375 5.25481 21.375C5.45464 21.375 5.65114 21.3244 5.82607 21.2281L19.3058 12.8909C19.3282 12.8771 19.3512 12.8645 19.3748 12.8531C19.5413 12.7729 19.6817 12.6474 19.78 12.491C19.8782 12.3345 19.9304 12.1535 19.9304 11.9688C19.9304 11.7841 19.8782 11.6031 19.78 11.4466C19.6817 11.2902 19.5413 11.1647 19.3748 11.0845C19.3519 11.0735 19.3296 11.0613 19.308 11.048L5.82704 2.7724C5.65187 2.67574 5.45501 2.625 5.25481 2.625Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-play-arrow') === undefined) {
  customElements.define('cat-icon-play-arrow', BlocksIconPlayArrow);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-play-arrow': BlocksIconPlayArrow;
  }
}
